import { ROUTE_NAMES } from '@/constant/routes'
import { removeUrlParam } from '@/utils/url.utils'
import { isPptDialog } from '@/business-logic/ppt-dialog'

// AHA-47221: the add-in's editor entrance.
//
// /ppt-addin/edit-slides/:id renders exactly what /presentation/:id renders — same component,
// same presentation — and differs only in what the pane is FOR: it is the editor, opened with
// the editing tools and the slide picker both live, rather than the presenting canvas.
//
// It exists as a route rather than as a store flag because the state has to survive a task-pane
// reload. PowerPoint reloads the pane on its own schedule, and a store flag does not come back
// from that — the URL does.
//
// It does not unbind the frame: the user is choosing what to do NEXT with the presentation they
// are presenting from, not leaving it. That is the whole difference from the back arrow
// (BackToEditorButton), which clears dataSlidePPT on the way out.
export const PPT_ADDIN_EDITOR_ROUTES = [
  ROUTE_NAMES.PPT_ADDIN_EDIT_SLIDES,
]

// The path the route above resolves to, minus its :id.
//
// Exported for the one caller that cannot ask the router: services/office runs on the cold-load
// path, where it reads window.location.href directly because the router has not necessarily
// settled on the booted route yet (see coldLoadNavigated).
export const PPT_ADDIN_EDITOR_PATH = '/ppt-addin/edit-slides/'

// "This WINDOW is showing the add-in's editor entrance", asked of the raw URL rather than of the
// router, for that same cold-load caller.
export const isOnPptAddinEditorUrl = () => {
  return window.location.href.indexOf(PPT_ADDIN_EDITOR_PATH) !== -1
}

// "This route is the add-in's editor entrance." Used where the app tests for the presentation
// editor by route name and would otherwise treat it as somewhere else entirely.
export const isPptAddinEditorRoute = (routeName) => {
  return PPT_ADDIN_EDITOR_ROUTES.includes(routeName)
}

// AHA-47221: "this window opened straight onto a presentation that had just been created."
//
// The embed list's "New presentation" button makes the presentation and opens the add-in's editor
// entrance on it in an Office dialog, carrying `new=true` — the same param the dashboard's own
// create sends, which the editor reads as "made a moment ago" (PresentationEditor).
//
// Asked of the ROUTE rather than of state.isNewPresentation, which PresentationEditor clears as
// soon as the user picks a slide type: what this answers is how the WINDOW was opened, and that
// does not change while it is open. It survives the address bar losing the param on the way in
// too — that strip is a replaceState, which does not re-run the router.
export const NEW_PRESENTATION_QUERY = 'new'

export const isPptAddinNewPresentationRoute = (route) => {
  if (!route || !isPptAddinEditorRoute(route.name)) return false
  return String((route.query || {})[NEW_PRESENTATION_QUERY]) === 'true'
}

// AHA-47221: "the user came here to ADD slides", carried in the URL.
//
// The editor's sidebar opens with the first selectable slide already ticked (see PptEditorMixin).
// That is a helpful starting position for someone who arrived to pick slides for the deck, and
// the wrong one for someone who arrived to edit: a tick they did not make, on a presentation
// they only meant to look at, which the Add bar then offers to put into PowerPoint.
//
// In the URL rather than in the store for the same reason the edit-mode route is: the editor can
// be opened in an Office DIALOG, which is a separate browser context with its own store, so the
// URL is the only thing the two ends share — and it survives PowerPoint reloading the task pane.
export const PPT_ADD_SLIDES_QUERY = 'addSlide'

// The query object to push, so no caller has to spell the value out. Empty when the intent is
// not "add slides": an absent param and an explicit false one mean the same thing here, and
// leaving it out keeps the URL saying only what it has to.
export const pptAddSlidesQuery = (addSlides) => {
  return addSlides ? { [PPT_ADD_SLIDES_QUERY]: 'true' } : {}
}

export const PPT_VIEW_ONLY_QUERY = 'viewOnly'

export const pptViewOnlyQuery = (viewOnly) => {
  return viewOnly ? { [PPT_VIEW_ONLY_QUERY]: 'true' } : {}
}

const VIEW_ONLY_STORAGE_KEY = 'pptViewOnlyDialog'
let viewOnlyDialogLatched = false

const readViewOnlyLatch = () => {
  try {
    return window.sessionStorage.getItem(VIEW_ONLY_STORAGE_KEY) === 'true'
  } catch (error) {
    return false
  }
}

const writeViewOnlyLatch = () => {
  try {
    window.sessionStorage.setItem(VIEW_ONLY_STORAGE_KEY, 'true')
  } catch (error) {
    // Storage blocked: the in-memory latch still covers navigation inside this dialog.
  }
}

export const isPptViewOnlyDialog = () => {
  if (!isPptDialog()) return false
  if (viewOnlyDialogLatched) return true
  const marked = new URLSearchParams(window.location.search).get(PPT_VIEW_ONLY_QUERY) === 'true'
  if (marked) writeViewOnlyLatch()
  viewOnlyDialogLatched = marked || readViewOnlyLatch()
  return viewOnlyDialogLatched
}

// AHA-48017: the presenting URL for an add-in frame that has just entered present mode from an editor.
//
// Every in-editor way into present mode — the selection bar, and the older "Select this slide" /
// "Select presentation" buttons — gets there IN PLACE, with a store dispatch, because a
// router.replace from inside the mounted editor changes nothing on screen (see
// ppt-add-selected-slides). That left the address bar on an editor URL while the canvas presented,
// and a reload believes the URL, so the frame came back as the editor. The reload is not rare: the
// context menu's Reload does one, PowerPoint reloads the pane on its own schedule, and every OTHER
// frame reloads when results are reset (ResetSingleSlideResultsMixin).
//
// Two editor URLs lead here, and both have to be handled:
//   - /ppt-addin/edit-slides/:id — the add-in's own editor entrance. services/office leaves a frame
//     on it by design, so the path itself has to move.
//   - /presentation/:id with no ?presenting=true — the app's editor, reached from the dashboard's
//     presentation list inside the pane. Only the query is missing.
//
// Put back together by concatenation rather than by writing through a URL object, for the reason
// removeUrlParam gives: Office identifies the frame by `_host_Info` in the query and `_xdm_Info` in
// the fragment, and those must come back byte-for-byte, not re-encoded. Reading is a different
// matter — see splitUrl. Only `addSlide` is dropped — it means "came here to add slides", which the
// frame is no longer doing — and `presenting` is replaced rather than added, so it is never there
// twice.
const PRESENTATION_PATH = '/presentation/'

// The URL in the pieces these two functions rewrite.
//
// `new URL` to READ, concatenation to write. Reading is safe: pathname, search and hash come back
// exactly as the browser normalized them, `_host_Info=Powerpoint$Win32$...` and
// `_xdm_Info=2|h1|https%3A%2F%2F...|` included. Writing through URLSearchParams is what is not —
// it re-encodes on the way out (`$`→`%24`, `|`→`%7C`, a space→`+`), which is precisely the damage
// the byte-for-byte rule above is about. So nothing here goes back through the serializer.
//
// null for anything that is not an absolute URL, which every caller reports back as "leave this
// alone": the only href in production is window.location.href, and a caller that passes something
// else gets its own string back rather than a throw.
//
// protocol + host rather than origin, which is the string "null" for schemes that have no host.
const splitUrl = (href) => {
  try {
    const url = new URL(href)
    return {
      base: `${url.protocol}//${url.host}`,
      path: url.pathname,
      query: url.search.slice(1),
      hash: url.hash,
    }
  } catch {
    return null
  }
}

// "This URL already says presenting." URLSearchParams on the READ side only, where decoding is
// exactly what is wanted and nothing is serialized back out.
const isPresentingQuery = (href) => {
  const parts = splitUrl(href)
  return !!parts && new URLSearchParams(parts.query).get('presenting') === 'true'
}

const presentingUrlFromEditorUrl = (href, presentationId) => {
  const stripped = [PPT_ADD_SLIDES_QUERY, 'presenting'].reduce(
    (url, param) => removeUrlParam(param, url),
    href,
  )
  const parts = splitUrl(stripped)
  if (!parts) return href
  const {
    base, path, query, hash,
  } = parts

  let editorAt = path.indexOf(PPT_ADDIN_EDITOR_PATH)
  if (editorAt === -1) {
    // Already on the presenting URL: nothing to fix, and nothing to reorder.
    if (isPresentingQuery(href)) return href
    editorAt = path.indexOf(PRESENTATION_PATH)
  }
  if (editorAt === -1) return href

  // Everything before the editor path is kept as it was — any public path the app is served under.
  return `${base}${path.slice(0, editorAt)}${PRESENTATION_PATH}${presentationId}?presenting=true${query ? `&${query}` : ''}${hash}`
}

// Write the address bar, without the router.
//
// A raw history.replaceState, not a router navigation: a navigation is the very replace that does
// nothing useful from inside the mounted editor, and it would re-run route guards for a frame that
// is not going anywhere. The router's own idea of the route is left as it was — only what a reload
// reads changes. history.state is passed through so the router's scroll-position key survives, and
// an unchanged URL is left alone, so nothing is written for a frame this does not apply to.
const replaceAddressBar = (next) => {
  if (!window.history?.replaceState) return
  if (next === window.location.href) return
  window.history.replaceState(window.history.state, '', next)
}

// Point the address bar at the presenting URL once a frame has entered present mode in place, so
// the next reload lands where the user now is. A no-op when the URL is already the presenting one,
// or is not an editor URL at all.
export const replaceEditorUrlWithPresentingUrl = (presentationId) => {
  replaceAddressBar(presentingUrlFromEditorUrl(window.location.href, presentationId))
}

// AHA-48017: the inverse, for a frame that LEAVES present mode in place.
//
// The escape arrow's add-in branch (BackToEditorButton) clears dataSlidePPT and dispatches
// EDIT_MODE without a router navigation, exactly as the ways in enter present mode without one.
// So it has to take the address bar back with it, or the rewrite above simply points the lie the
// other way: the pane is the editor while the URL says presenting, and the next reload — the
// context menu's Reload, PowerPoint's own pane reload, a results reset from another frame — brings
// back the presenting canvas of a frame that is no longer bound to a slide at all.
//
// WHICH editor URL it goes back to is the router's to say, not this module's. The rewrite on the
// way in deliberately left router.currentRoute where it was, so the route the pane is still
// mounted on IS the URL it came from: the add-in's own editor entrance for a frame that started
// there, and the app editor for one that came from the dashboard list inside the pane. Taking the
// route name rather than re-deriving it keeps the add-in path — which nothing else in the app ever
// navigates to, so nothing else would bring it back — restorable.
//
// `addSlide` is deliberately NOT put back: that selection was consumed on the way in.
const editorUrlFromPresentingUrl = (href, presentationId, routeName) => {
  const stripped = removeUrlParam('presenting', href)
  const parts = splitUrl(stripped)
  if (!parts) return href
  const {
    base, path, query, hash,
  } = parts

  let presentingAt = path.indexOf(PRESENTATION_PATH)
  if (presentingAt === -1) presentingAt = path.indexOf(PPT_ADDIN_EDITOR_PATH)
  if (presentingAt === -1) return href

  const editorPath = isPptAddinEditorRoute(routeName)
    ? `${PPT_ADDIN_EDITOR_PATH}${presentationId}`
    : `${PRESENTATION_PATH}${presentationId}`

  // Everything before the path is kept as it was — any public path the app is served under.
  return `${base}${path.slice(0, presentingAt)}${editorPath}${query ? `?${query}` : ''}${hash}`
}

export const replacePresentingUrlWithEditorUrl = (presentationId, routeName) => {
  replaceAddressBar(editorUrlFromPresentingUrl(window.location.href, presentationId, routeName))
}
